<script>
import constants from '@/constants';

export default {
    data() {
        return {
            constants,
            debounceTimer: null,
            localFilters: this.getDefaultFilters(),
        };
    },
    computed: {
        filters() {
            return this.$store.getters.SEARCH_QUERY_ANALYTICS_FILTERS;
        },
        activeFilterCount() {
            let count = 0;
            if (this.localFilters.userIds) count++;
            if (this.localFilters.hasSearchQuery !== null) count++;
            if (this.localFilters.manualFiltersUsed !== null) count++;
            if (this.localFilters.tagsUsed !== null) count++;
            if (this.localFilters.customFiltersUsed !== null) count++;
            if (this.localFilters.smartFiltersUsed !== null) count++;
            if (this.localFilters.hqOnlyUsed !== null) count++;
            return count;
        },
    },
    watch: {
        filters: {
            handler(newFilters) {
                this.localFilters = { ...newFilters };
            },
            deep: true,
        },
    },
    mounted() {
        this.localFilters = { ...this.filters };
    },
    beforeUnmount() {
        clearTimeout(this.debounceTimer);
    },
    methods: {
        debouncedApplyFilters() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.applyFilters();
            }, constants.DEBOUNCE_DELAY_MS);
        },
        applyFilters() {
            this.$store.commit(
                'SET_SEARCH_QUERY_ANALYTICS_FILTERS',
                this.localFilters
            );
            this.$store.commit('SET_SEARCH_QUERY_ANALYTICS_PAGE', 1);
            this.$store.dispatch('loadSearchQueryAnalytics');
        },
        getDefaultFilters() {
            return {
                userIds: '',
                hasSearchQuery: null,
                manualFiltersUsed: null,
                tagsUsed: null,
                customFiltersUsed: null,
                smartFiltersUsed: null,
                hqOnlyUsed: null,
            };
        },
        clearFilters() {
            this.localFilters = this.getDefaultFilters();
            this.$store.commit('RESET_SEARCH_QUERY_ANALYTICS_FILTERS');
            this.$store.dispatch('loadSearchQueryAnalytics');
        },
        handleBooleanFilterChange(filterName, event) {
            const value = event.target.value;
            if (value === constants.FILTER_ALL) {
                this.localFilters[filterName] = null;
            } else if (value === 'true') {
                this.localFilters[filterName] = true;
            } else if (value === 'false') {
                this.localFilters[filterName] = false;
            }
            this.applyFilters();
        },
        getBooleanFilterValue(filterName) {
            const value = this.localFilters[filterName];
            if (value === null) return constants.FILTER_ALL;
            return value.toString();
        },
    },
};
</script>

<template>
    <div class="filters_container">
        <div class="filters_header">
            <label class="filter_title">Filters</label>
            <button
                v-if="activeFilterCount > 0"
                @click="clearFilters"
                class="clear_button"
            >
                Clear All ({{ activeFilterCount }})
            </button>
        </div>

        <div class="filters_grid">
            <div class="filter_field">
                <label class="mb_5">User IDs:</label>
                <input
                    v-model="localFilters.userIds"
                    type="text"
                    placeholder="e.g., 1,5,10"
                    @input="debouncedApplyFilters"
                />
            </div>

            <div class="filter_field">
                <label class="mb_5">Search Query:</label>
                <select
                    :value="getBooleanFilterValue('hasSearchQuery')"
                    @change="handleBooleanFilterChange('hasSearchQuery', $event)"
                >
                    <option :value="constants.FILTER_ALL">All</option>
                    <option :value="'true'">Yes</option>
                    <option :value="'false'">No</option>
                </select>
            </div>

            <div class="filter_field">
                <label class="mb_5">Filters:</label>
                <select
                    :value="getBooleanFilterValue('manualFiltersUsed')"
                    @change="handleBooleanFilterChange('manualFiltersUsed', $event)"
                >
                    <option :value="constants.FILTER_ALL">All</option>
                    <option :value="'true'">Yes</option>
                    <option :value="'false'">No</option>
                </select>
            </div>

            <div class="filter_field">
                <label class="mb_5">Tags:</label>
                <select
                    :value="getBooleanFilterValue('tagsUsed')"
                    @change="handleBooleanFilterChange('tagsUsed', $event)"
                >
                    <option :value="constants.FILTER_ALL">All</option>
                    <option :value="'true'">Yes</option>
                    <option :value="'false'">No</option>
                </select>
            </div>

            <div class="filter_field">
                <label class="mb_5">Custom Filters:</label>
                <select
                    :value="getBooleanFilterValue('customFiltersUsed')"
                    @change="handleBooleanFilterChange('customFiltersUsed', $event)"
                >
                    <option :value="constants.FILTER_ALL">All</option>
                    <option :value="'true'">Yes</option>
                    <option :value="'false'">No</option>
                </select>
            </div>

            <div class="filter_field">
                <label class="mb_5">Smart Filters:</label>
                <select
                    :value="getBooleanFilterValue('smartFiltersUsed')"
                    @change="handleBooleanFilterChange('smartFiltersUsed', $event)"
                >
                    <option :value="constants.FILTER_ALL">All</option>
                    <option :value="'true'">Yes</option>
                    <option :value="'false'">No</option>
                </select>
            </div>

            <div class="filter_field">
                <label class="mb_5">HQ Only:</label>
                <select
                    :value="getBooleanFilterValue('hqOnlyUsed')"
                    @change="handleBooleanFilterChange('hqOnlyUsed', $event)"
                >
                    <option :value="constants.FILTER_ALL">All</option>
                    <option :value="'true'">Yes</option>
                    <option :value="'false'">No</option>
                </select>
            </div>
        </div>
    </div>
</template>

<style scoped>
.filters_container {
    background: var(--background-color);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 15px;
}

.filters_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
}

.filter_title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-color);
}

.clear_button {
    padding: 6px 12px;
    background: var(--background-hover);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    color: var(--text-color);
    transition: all 0.2s ease;
}

.clear_button:hover {
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
}

.filters_grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
}

.filter_field select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.2s ease;
}

.filter_field select:hover {
    border-color: var(--primary-color);
}

.filter_field select:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.filter_field {
    display: flex;
    flex-direction: column;
}

.filter_field label {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-color);
}

.mb_5 {
    margin-bottom: 5px;
}

.filter_field input {
    padding: 8px 12px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 14px;
}

.filter_field input:focus {
    outline: none;
    border-color: var(--primary-color);
}

.filter_field input::placeholder {
    color: var(--text-color-secondary);
}
</style>
